import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { ArrowRight, Loader2, LogIn, Eye, EyeOff } from "lucide-react";
import { ASSETS } from "@/lib/constants";
import { track, EVENTS } from "@/lib/analytics";
import { GoogleSignInButton, AuthDivider } from "@/components/GoogleSignInButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "grc_mlo_token";

export default function MLOLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  // If already logged in, bounce to dashboard
  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return;
    axios
      .get(`${API}/mlo/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
      .then(() => nav("/mlodashboard", { replace: true }))
      .catch(() => localStorage.removeItem(TOKEN_KEY));
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setErr("Email and password required.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const { data } = await axios.post(`${API}/mlo/auth/login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      track(EVENTS.MLO_LOGIN, { mlo_slug: data.user?.mlo_slug || "unknown" });
      toast.success(`Welcome back, ${data.user.mlo_slug || "MLO"}`);
      nav("/mlodashboard", { replace: true });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-linen flex items-center justify-center px-4 antialiased">
      <div className="w-full max-w-md" data-testid="mlo-login-page">
        <Link to="/" className="flex justify-center mb-8">
          <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-10 w-auto" />
        </Link>

        <div className="bg-white border border-linen-border rounded-3xl shadow-soft p-8">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.22em] uppercase text-teal">
              <LogIn size={13} />
              Loan officer sign-in
            </span>
            <h1 className="mt-3 font-heading font-medium text-3xl text-navy leading-tight">
              Welcome back.
            </h1>
            <p className="mt-2 text-sm text-ink-500">
              Sign in to edit your branded page and access the network toolkit.
            </p>
          </div>

          <div className="mt-7">
            <GoogleSignInButton returnPath="/mlodashboard" testid="mlo-login-google" />
            <AuthDivider label="or use your password" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-500">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                data-testid="mlo-login-email"
                className="mt-1 w-full rounded-xl border border-linen-border p-3 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none transition-colors"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-ink-500">Password</span>
              <div className="relative mt-1">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  data-testid="mlo-login-password"
                  className="w-full rounded-xl border border-linen-border p-3 pr-11 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="mlo-login-toggle-password"
                  className="absolute inset-y-0 right-2 flex items-center px-2 text-ink-500 hover:text-navy"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </label>

            {err && (
              <p className="text-sm text-red-600" data-testid="mlo-login-error">
                {err}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="mlo-login-submit"
              className="w-full inline-flex justify-center items-center gap-2 bg-navy hover:bg-[#173a5f] text-white font-semibold text-sm px-6 py-3 rounded-full shadow-cta transition-all disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Sign in
              <ArrowRight size={15} />
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-ink-500">
            Need an account?{" "}
            <a href="mailto:daren@greatratecheck.com" className="text-teal hover:underline">
              Contact your admin
            </a>
          </p>
        </div>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}
